import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMp3Player } from './Mp3PlayerContext';
import { trackUxEvent } from '../../services/uxEvents';
import {
  clampPlayerPosition,
  formatLcdTime,
  radioArtistHref,
  radioArtistLabel,
  readStoredPosition,
  writeStoredPosition,
} from './services/mp3PlayerEngine';
import './Mp3PlayerSkin.css';

const DEFAULT_LEFT = 12;
const DOCK_GAP_PX = 8;

function visHeights(playing, time) {
  if (!playing) return [2, 3, 2, 4, 3, 2, 3, 2];
  const t = time * 7.3;
  return [0, 1, 2, 3, 4, 5, 6, 7].map((i) => {
    const n = Math.abs(Math.sin(t + i * 0.7));
    return 2 + Math.round(n * 12);
  });
}

function readPlayerBottomInset() {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return 84 + DOCK_GAP_PX;
  }
  const nav = parseFloat(
    getComputedStyle(document.documentElement).getPropertyValue('--bottom-nav-height'),
  ) || 84;
  let safe = 0;
  try {
    const probe = document.createElement('div');
    probe.style.cssText = 'position:absolute;visibility:hidden;pointer-events:none;padding-bottom:env(safe-area-inset-bottom,0px)';
    document.body.appendChild(probe);
    safe = parseFloat(getComputedStyle(probe).paddingBottom) || 0;
    probe.remove();
  } catch {
    /* ignore */
  }
  return nav + safe + DOCK_GAP_PX;
}

function clampToViewport(x, y, box, bottomInset = readPlayerBottomInset()) {
  return clampPlayerPosition({
    x,
    y,
    width: box?.width || 280,
    height: box?.height || 36,
    viewportW: window.innerWidth,
    viewportH: window.innerHeight,
    bottomInset,
  });
}

export default function Mp3PlayerSkin() {
  const {
    enabled,
    isAdmin,
    tracks,
    index,
    currentTrack,
    isPlaying,
    currentTime,
    duration,
    volume,
    shade,
    loading,
    buffering,
    hidden,
    play,
    pause,
    stop,
    togglePlay,
    playNext,
    playPrev,
    seek,
    setVolume,
    toggleShade,
    selectTrack,
    close,
    reloadPlaylist,
  } = useMp3Player();

  const rootRef = useRef(null);
  const dragRef = useRef(null);
  const [placed, setPlaced] = useState(null);
  const [showPlaylist, setShowPlaylist] = useState(true);

  useEffect(() => {
    if (!enabled || hidden || shade || !showPlaylist) return;
    reloadPlaylist();
  }, [enabled, hidden, shade, showPlaylist, reloadPlaylist]);

  useEffect(() => {
    const stored = readStoredPosition();
    if (!stored) return;
    const box = rootRef.current?.getBoundingClientRect();
    const next = clampToViewport(stored.left, stored.top, box);
    setPlaced({ left: next.x, top: next.y });
  }, []);

  const onTitlePointerDown = useCallback((e) => {
    if (e.button != null && e.button !== 0) return;
    if (e.target.closest('button')) return;
    const el = rootRef.current;
    if (!el) return;
    const box = el.getBoundingClientRect();
    dragRef.current = {
      dx: e.clientX - box.left,
      dy: e.clientY - box.top,
      width: box.width,
      height: box.height,
      bottomInset: readPlayerBottomInset(),
    };
    e.currentTarget.setPointerCapture?.(e.pointerId);
  }, []);

  const onTitlePointerMove = useCallback((e) => {
    const drag = dragRef.current;
    if (!drag) return;
    const next = clampToViewport(
      e.clientX - drag.dx,
      e.clientY - drag.dy,
      drag,
      drag.bottomInset,
    );
    setPlaced({ left: next.x, top: next.y });
  }, []);

  const onTitlePointerUp = useCallback((e) => {
    if (!dragRef.current) return;
    dragRef.current = null;
    try {
      e.currentTarget.releasePointerCapture?.(e.pointerId);
    } catch {
      /* ignore */
    }
    const el = rootRef.current;
    if (!el) return;
    const box = el.getBoundingClientRect();
    const next = clampToViewport(box.left, box.top, box);
    const pos = { left: next.x, top: next.y };
    writeStoredPosition(pos);
    setPlaced(pos);
  }, []);

  const onSeek = (e) => {
    seek(Number(e.target.value) || 0);
  };

  const onVolume = (e) => {
    setVolume((Number(e.target.value) || 0) / 100);
  };

  if (!enabled || hidden) return null;

  const title = currentTrack?.title || (loading ? 'LOADING…' : 'MapToBe Radio');
  const artistName = radioArtistLabel(currentTrack);
  const artistHref = radioArtistHref(currentTrack);
  const shadeTitle = currentTrack
    ? `${artistName} — ${title}`
    : title;
  const lcdTime = buffering ? 'BUF' : formatLcdTime(currentTime);
  const maxDur = duration || currentTrack?.durationSec || 0;
  const bars = visHeights(isPlaying, currentTime);
  const style = placed
    ? { left: placed.left, top: placed.top, bottom: 'auto' }
    : { left: DEFAULT_LEFT };

  return (
    <div
      ref={rootRef}
      className={`mp3-player${shade ? ' mp3-player--shade' : ''}${placed ? ' mp3-player--placed' : ''}`}
      style={style}
      role="region"
      aria-label="MapToBe Radio"
      aria-busy={buffering}
    >
      <div
        className="mp3-player__title"
        onPointerDown={onTitlePointerDown}
        onPointerMove={onTitlePointerMove}
        onPointerUp={onTitlePointerUp}
        onPointerCancel={onTitlePointerUp}
        onDoubleClick={toggleShade}
      >
        <span className="mp3-player__brand">MAPTOBE RADIO</span>
        <span className="mp3-player__title-spacer" />
        <button
          type="button"
          className="mp3-player__tb"
          aria-label={shade ? 'Expand player' : 'Collapse to shade'}
          onClick={toggleShade}
        >
          ▭
        </button>
        <button
          type="button"
          className="mp3-player__tb mp3-player__tb--close"
          aria-label="Close MapToBe Radio"
          onClick={() => {
            close();
            trackUxEvent({ name: 'radio_close' });
          }}
        >
          X
        </button>
      </div>

      {shade ? (
        <div className="mp3-player__shade-row">
          <span className="mp3-player__lcd mp3-player__lcd--shade" aria-hidden="true">
            {lcdTime}
          </span>
          <span className="mp3-player__shade-title" title={shadeTitle}>{shadeTitle}</span>
          <div className="mp3-player__shade-btns">
            <button type="button" className="mp3-player__btn" aria-label="Previous" onClick={playPrev}>⏮</button>
            <button
              type="button"
              className="mp3-player__btn"
              aria-label={isPlaying ? 'Pause' : 'Play'}
              onClick={togglePlay}
            >
              {isPlaying ? '⏸' : '▶'}
            </button>
            <button type="button" className="mp3-player__btn" aria-label="Next" onClick={playNext}>⏭</button>
          </div>
        </div>
      ) : (
        <>
          <div className="mp3-player__main">
            <div className="mp3-player__lcd" aria-hidden="true">
              <span className="mp3-player__time">{lcdTime}</span>
              <div className={`mp3-player__vis${buffering ? ' is-buffering' : ''}`}>
                {bars.map((h, i) => (
                  <span
                    key={i}
                    className="mp3-player__bar"
                    style={{ height: `${h}px` }}
                  />
                ))}
              </div>
            </div>
            <p className="mp3-player__song" title={title}>{title}</p>
            {artistHref ? (
              <Link
                to={artistHref}
                className="mp3-player__artist"
                onPointerDown={(e) => e.stopPropagation()}
              >
                {artistName}
              </Link>
            ) : (
              <p className="mp3-player__artist mp3-player__artist--plain">{artistName}</p>
            )}
            <div className="mp3-player__sliders">
              <label className="mp3-player__vol">
                <span>VOL</span>
                <input
                  type="range"
                  min="0"
                  max="100"
                  value={Math.round(volume * 100)}
                  onChange={onVolume}
                  aria-label="Volume"
                />
              </label>
              <label className="mp3-player__seek">
                <span>POS</span>
                <input
                  type="range"
                  min="0"
                  max={Math.max(1, Math.floor(maxDur))}
                  value={Math.min(Math.floor(currentTime), Math.floor(maxDur) || 0)}
                  onChange={onSeek}
                  aria-label="Seek"
                />
              </label>
            </div>
            <div className="mp3-player__transport">
              <button type="button" className="mp3-player__btn" aria-label="Previous" onClick={playPrev}>⏮</button>
              <button type="button" className="mp3-player__btn" aria-label="Play" onClick={play}>▶</button>
              <button type="button" className="mp3-player__btn" aria-label="Pause" onClick={pause}>⏸</button>
              <button type="button" className="mp3-player__btn" aria-label="Stop" onClick={stop}>⏹</button>
              <button type="button" className="mp3-player__btn" aria-label="Next" onClick={playNext}>⏭</button>
              <button
                type="button"
                className={`mp3-player__pl${showPlaylist ? ' is-on' : ''}`}
                aria-pressed={showPlaylist}
                aria-label="Toggle playlist"
                onClick={() => setShowPlaylist((v) => !v)}
              >
                PL
              </button>
            </div>
          </div>

          {showPlaylist ? (
            <div className="mp3-player__pl-win">
              <div className="mp3-player__pl-head">
                <span>MAPTOBE RADIO</span>
              </div>
              <ol className="mp3-player__pl-list">
                {tracks.length === 0 ? (
                  <li className="mp3-player__pl-empty">
                    {loading
                      ? 'Loading…'
                      : isAdmin
                        ? (
                          <>
                            Add tracks in{' '}
                            <Link to="/adminhub/music-manager" className="mp3-player__pl-link">
                              Music Manager
                            </Link>
                          </>
                          )
                        : 'No tracks'}
                  </li>
                ) : (
                  tracks.map((track, i) => (
                    <li
                      key={track.id}
                      className={`mp3-player__pl-row${i === index ? ' is-current' : ''}`}
                    >
                      <button
                        type="button"
                        className="mp3-player__pl-name"
                        onClick={() => selectTrack(i)}
                      >
                        {String(i + 1).padStart(2, '0')}. {track.title}
                        {track.artistName ? ` — ${track.artistName}` : ''}
                      </button>
                      <span className="mp3-player__pl-dur">
                        {formatLcdTime(track.durationSec)}
                      </span>
                    </li>
                  ))
                )}
              </ol>
            </div>
          ) : null}
        </>
      )}
    </div>
  );
}
